import { APP_NAME } from '@circa/shared/config';
import { ArrowCircleUp } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { api, apiUrl } from '@/core/api';
import { isAndroid } from '@/core/platform';
import { getServerUrl } from '@/core/session';
import { compareVersions } from '@/core/version';
import { Button } from './components';
import { toast } from './overlays';
import './zone.css';

// "A new version is available" for the Android app. The website runs the live site, so it's
// always current; the APK bundles the whole app, so any newer release is worth installing.
// The server keeps the latest APK (scripts/publish-apk.mjs) and the app installs it itself.

const DISMISS_KEY = 'circa.updateDismissed';
const CHECK_EVERY_MS = 6 * 3_600_000;

async function installedVersion(): Promise<string | null> {
  // The installed APK's own version (what Android installed), not what the bundle thinks.
  try {
    const { App } = await import('@capacitor/app');
    const { version } = await App.getInfo();
    if (version) return version;
  } catch {}
  return __APP_VERSION__;
}

async function checkForUpdate(): Promise<string | null> {
  if (!isAndroid || !getServerUrl()) return null;
  const installed = await installedVersion();
  if (!installed) return null;
  const res = await fetch(apiUrl('/api/app/version'), { cache: 'no-store' });
  if (!res.ok) return null;
  const latest = (await res.json()) as { android?: string | null };
  if (!latest.android || compareVersions(installed, latest.android) >= 0) return null;
  return latest.android;
}

function dismissed(): string | null {
  try {
    return localStorage.getItem(DISMISS_KEY);
  } catch {
    return null;
  }
}

export function UpdatePrompt() {
  const [offer, setOffer] = useState<string | null>(null);
  const [installing, setInstalling] = useState(false);
  useEffect(() => {
    if (!isAndroid) return;
    let alive = true;
    const run = () =>
      void checkForUpdate()
        .then((v) => {
          if (alive) setOffer(v && v !== dismissed() ? v : null);
        })
        .catch(() => {});
    const first = setTimeout(run, 5000);
    const every = setInterval(run, CHECK_EVERY_MS);
    return () => {
      alive = false;
      clearTimeout(first);
      clearInterval(every);
    };
  }, []);
  if (!offer) return null;
  const update = async () => {
    setInstalling(true);
    try {
      await installAndroidUpdate();
    } catch (err) {
      toast((err as Error).message || 'Couldn’t update', { kind: 'error' });
    } finally {
      setInstalling(false);
    }
  };
  const later = () => {
    try {
      localStorage.setItem(DISMISS_KEY, offer);
    } catch {}
    setOffer(null);
  };
  return createPortal(
    <div className="zone-prompt" role="status">
      <ArrowCircleUp size={18} className="zone-prompt__icon" />
      <div className="zone-prompt__text">
        {APP_NAME} {offer} is available. It installs over this one and keeps your data.
      </div>
      <div className="zone-prompt__actions">
        <Button size="sm" variant="primary" loading={installing} onClick={() => void update()}>
          Update
        </Button>
        <Button size="sm" variant="ghost" onClick={later}>
          Later
        </Button>
      </div>
    </div>,
    document.body,
  );
}

interface ApkInstaller {
  canInstall(): Promise<{ allowed: boolean }>;
  openInstallSettings(): Promise<void>;
  install(o: { path: string }): Promise<void>;
}

/** Download the new APK from this server and hand it to Android's installer. */
async function installAndroidUpdate() {
  const { registerPlugin } = await import('@capacitor/core');
  const installer = registerPlugin<ApkInstaller>('ApkInstaller');
  const { allowed } = await installer.canInstall();
  if (!allowed) {
    await installer.openInstallSettings();
    throw new Error(`Allow ${APP_NAME} to install updates, then tap Update again.`);
  }
  const buf = await api<ArrayBuffer>('/api/app/android.apk');
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000)
    bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  const { Filesystem, Directory } = await import('@capacitor/filesystem');
  const file = await Filesystem.writeFile({
    path: 'circa-update.apk',
    data: btoa(bin),
    directory: Directory.Cache,
  });
  await installer.install({ path: file.uri });
}
